/**
 * "Connect 𝕏" — OAuth 2.0 Authorization Code flow with PKCE.
 *
 * The browser starts the flow; the code→token exchange happens in the
 * Netlify function at /.netlify/functions/x-auth (X's token endpoint has no
 * CORS headers, so it cannot be called directly from a page).
 *
 * Enable by setting VITE_X_CLIENT_ID in the frontend env, plus X_CLIENT_ID /
 * X_CLIENT_SECRET on the Netlify side. Without it the UI falls back to typing
 * a handle by hand (unverified).
 */

const CLIENT_ID = (import.meta.env.VITE_X_CLIENT_ID as string | undefined) ?? "";
const TOKEN_ENDPOINT =
  (import.meta.env.VITE_X_TOKEN_ENDPOINT as string | undefined) ??
  "/.netlify/functions/x-auth";

const VERIFIER_KEY = "wheel-x-verifier";
const STATE_KEY = "wheel-x-state";
const TOKEN_KEY = "wheel-x-token"; // user's access token, for posting on their behalf
const POST_ENDPOINT =
  (import.meta.env.VITE_X_POST_ENDPOINT as string | undefined) ??
  "/.netlify/functions/x-post";

export const xLoginAvailable = Boolean(CLIENT_ID);

/** The signed-in user's X access token (write-capable), if we have one. */
export function getXToken(): string | null {
  try {
    return sessionStorage.getItem(TOKEN_KEY);
  } catch {
    return null;
  }
}

export type XAccount = {
  handle: string;
  name: string;
  avatarUrl: string;
};

function randomString(bytes = 48): string {
  const arr = crypto.getRandomValues(new Uint8Array(bytes));
  return Array.from(arr, (b) => ("0" + b.toString(16)).slice(-2)).join("");
}

async function sha256Base64Url(input: string): Promise<string> {
  const digest = await crypto.subtle.digest("SHA-256", new TextEncoder().encode(input));
  let binary = "";
  for (const byte of new Uint8Array(digest)) binary += String.fromCharCode(byte);
  return btoa(binary).replace(/\+/g, "-").replace(/\//g, "_").replace(/=+$/, "");
}

function redirectUri(): string {
  return `${window.location.origin}${window.location.pathname}`;
}

/** Send the user to X to authorize. Returns to this page with ?code=… */
export async function startXLogin(): Promise<void> {
  if (!CLIENT_ID) throw new Error("X login is not configured");
  const verifier = randomString();
  const state = randomString(16);
  sessionStorage.setItem(VERIFIER_KEY, verifier);
  sessionStorage.setItem(STATE_KEY, state);

  const params = new URLSearchParams({
    response_type: "code",
    client_id: CLIENT_ID,
    redirect_uri: redirectUri(),
    // tweet.write + media.write let us post the PNL card on the user's behalf
    scope: "users.read tweet.read tweet.write media.write offline.access",
    state,
    code_challenge: await sha256Base64Url(verifier),
    code_challenge_method: "S256",
  });
  window.location.href = `https://x.com/i/oauth2/authorize?${params.toString()}`;
}

/**
 * Call once on page load. If we came back from X, exchanges the code for the
 * account and cleans the URL. Returns null when this is a normal page load.
 */
export async function completeXLogin(): Promise<XAccount | null> {
  const url = new URL(window.location.href);
  const code = url.searchParams.get("code");
  const state = url.searchParams.get("state");
  if (!code) return null;

  const verifier = sessionStorage.getItem(VERIFIER_KEY);
  const expectedState = sessionStorage.getItem(STATE_KEY);
  sessionStorage.removeItem(VERIFIER_KEY);
  sessionStorage.removeItem(STATE_KEY);

  // strip the oauth params either way so a refresh doesn't retry
  url.searchParams.delete("code");
  url.searchParams.delete("state");
  window.history.replaceState({}, "", url.toString());

  if (!verifier || !expectedState || state !== expectedState) {
    throw new Error("X login state mismatch — try again");
  }

  const res = await fetch(TOKEN_ENDPOINT, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ code, codeVerifier: verifier, redirectUri: redirectUri() }),
  });
  if (!res.ok) throw new Error("could not verify with X");
  const data = (await res.json()) as {
    username?: string;
    name?: string;
    avatarUrl?: string;
    accessToken?: string;
  };
  if (!data.username) throw new Error("X did not return an account");
  // stash the write-capable token so we can post the win card later
  try {
    if (data.accessToken) sessionStorage.setItem(TOKEN_KEY, data.accessToken);
  } catch {
    /* storage unavailable */
  }
  return {
    handle: data.username,
    name: data.name ?? data.username,
    avatarUrl: data.avatarUrl ?? "",
  };
}

/**
 * Post a tweet with an image on the signed-in user's behalf (X API v2). Goes
 * through our server (X's API has no browser CORS). Returns the tweet URL on
 * success. Throws if we have no token or the post fails.
 */
export async function postToX(text: string, imageDataUrl: string): Promise<string> {
  const token = getXToken();
  if (!token) throw new Error("not signed into X");
  const res = await fetch(POST_ENDPOINT, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ accessToken: token, text, imageDataUrl }),
  });
  const data = (await res.json().catch(() => ({}))) as { url?: string; error?: string };
  if (!res.ok || !data.url) throw new Error(data.error || "could not post to X");
  return data.url;
}
